/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppDispatch, RootState } from '@/store';
import {
  OperationResource,
  OperationTree,
  ProcessTypesTree,
  SiteTree,
  TreeItem,
  TSimulationMenuOptions,
  WipTree,
  WipTreeElement,
  WorkPlanTree,
} from '@/store/types';
import {
  Button,
  Input,
  Label,
  Table,
  Tooltip,
  useDebounceCallback,
} from '@data-products-and-ai/react-components';
import {
  TableHeaderType,
  TableRowData,
} from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';
import { TAnchorEl } from '@data-products-and-ai/react-components/lib/components/Layout/Menu/Menu';
import { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContextMenu } from '../ContextMenu';

import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import { changeExpanded } from '@/store/storeSlice';
import { produce } from 'immer';
import {
  ProductTreeRenderGeneric,
  TProductStructure,
} from '@/pages/Simulation/TableView/Operations/types';
import ScrapReworkPercentage from '@/components/ScrapReworkPercentage/ScrapReworkPercentage';
import MultiEditModal from '@/components/MultiEditModal/MultiEditModal';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
/**
 * Operations Component
 * Renders a table of the simulations operations with various functionalities such as filtering, expanding rows, and context menu actions
 * Manages the state for selected row, anchor element to render the context menu, and filter query
 */
const Operations = () => {
  /* const dispatch: AppDispatch = useDispatch(); */
  const { setSimulationParams } = useContext(SimulationCtx);
  const dispatch: AppDispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<TAnchorEl>(null);
  const [selectedRow, setSelectedRow] = useState<TableRowData | null>(null);

  const handleInputChangeDebounced = useDebounceCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFilterQuery(event.target.value);
      console.log(event.target.value);
    },
    1000,
  );
  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected),
  );

  const [filterQuery, setFilterQuery] = useState<string>('');

  const handleCustomDrawer = (
    event: React.MouseEvent<HTMLButtonElement | HTMLSpanElement, MouseEvent>,
    formItem: string,
    formArea: TSimulationMenuOptions,
  ) => {
    event.stopPropagation();

    setSimulationParams(
      produce((draft) => {
        draft.formItem = formItem;
        draft.formArea = formArea;
        draft.drawerOpen = true;
      }),
    );
  };

  /**
   * Handles the rendering of resources for an operation
   * Returns a JSX element displaying the resources and a button to manage resources
   *
   * @param {string} operation_id - The ID of the operation
   * @param {OperationResource[]} operation_resources - The resources of the operation
   */
  const handleResources = (
    operation_id: string,
    operation_resources: OperationResource[],
  ) => {
    const c = operation_resources.map(
      (item: OperationResource, idx: number) => {
        const resource =
          selectedScenario?.data.resources.byId[item.RESOURCE_ID.value];

        const resourceType =
          selectedScenario?.data.resource_types.byId[
            item.RESOURCE_TYPE_ID.value
          ]?.RESOURCE_TYPE.value;
        const isLastItem = idx === operation_resources.length - 1;
        const borderBottomStyle = isLastItem
          ? {}
          : { borderBottom: 'solid 1px #dadada' };

        if (!resource) return;
        return (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 1fr 1fr',
              padding: 5,
              gap: 20,
              ...borderBottomStyle,
            }}
          >
            <div style={{ paddingRight: 0 }}>
              {resource.RESOURCE_NAME.value}
            </div>
            <div style={{ textAlign: 'center' }}>{resourceType}</div>
            <div style={{ textAlign: 'center' }}>
              {
                selectedScenario?.data.shift_calendar.byId[
                  resource.RESOURCE_SHIFT_CALENDAR_ID.value
                ].RESOURCE_SHIFT_CALENDAR.value
              }
            </div>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
              {item.RESOURCE_PRIORITY.value}
            </div>
          </div>
        );
      },
    );

    return (
      <>
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 1fr 1fr',
              padding: 5,
              gap: 20,

              borderBottom: 'solid 1px #dadada',
              fontSize: 11,
              fontWeight: 'bold',
            }}
          >
            <div>Resource</div>
            <div style={{ textAlign: 'center' }}>Storage</div>
            <div style={{ textAlign: 'center' }}>Shift</div>
            <div style={{ textAlign: 'center' }}>Priority</div>
          </div>
          {c}
          <div
            style={{
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            <Button
              type="secondary"
              width={'100%'}
              onClick={(event) =>
                handleCustomDrawer(event, operation_id, 'operations_resources')
              }
            >
              Manage resources
            </Button>
          </div>
        </div>
      </>
    );
  };

  type TResourceCount = {
    item: OperationTree;
    is_open: boolean;
  };

  const calculateOperationTime = (key: string, item: OperationTree) => {
    return selectedScenario?.data.operations_resources.allIds
      .map((id) => {
        const obj = selectedScenario?.data.operations_resources.byId[id];
        if (
          obj.OPERATION_ID.value === item.OPERATION_ID.value &&
          selectedScenario?.data.resource_types.byId[obj.RESOURCE_TYPE_ID.value]
            .RESOURCE_TYPE.value !== 'STORAGE'
        ) {
          return (obj[key as keyof OperationResource] as TreeItem).value || '0';
        }
        return undefined;
      })
      .filter((value): value is string => value !== undefined)
      .reduce((acc: number, value: string) => acc + (parseInt(value) || 0), 0)
      .toString();
  };

  /**
   * ResourceCount Component
   * Renders the count of resources for an operation with a tooltip displaying the resources
   *
   * @param {TResourceCount} props - The properties for the resource count
   */
  const ResourceCount = ({ item, is_open }: TResourceCount) => {
    if (!is_open) return '';
    const filteredObjects: OperationResource[] = selectedScenario
      ? selectedScenario.data.operations_resources.allIds
          .map((id) => selectedScenario?.data.operations_resources.byId[id])
          .filter((obj) => obj.OPERATION_ID.value === item.OPERATION_ID.value)
          .sort(
            (a, b) =>
              parseInt(a.RESOURCE_PRIORITY.value) -
              parseInt(b.RESOURCE_PRIORITY.value),
          )
      : [];

    return (
      <>
        <Tooltip
          key={'Tooltip_Resources_' + item.OPERATION_ID}
          color="light"
          title={handleResources(item.OPERATION_ID.value, filteredObjects)}
          disableInteractive={false}
        >
          <div style={{ display: 'inline-block' }}>
            <Button type="headless" allCaps={false}>
              {filteredObjects?.length}
            </Button>
          </div>
        </Tooltip>
      </>
    );
  };

  /**
   * Transforms an operation tree item into a product tree render generic object
   *
   * @param {OperationTree} item - The operation tree item to be transformed
   * @returns {ProductTreeRenderGeneric} - The transformed product tree render generic object
   */
  const TransformProduct = (item: OperationTree) => {
    const newItem: ProductTreeRenderGeneric = {
      key: item.OPERATION.originalValue,
      title: item.OPERATION.value,
      is_wip: item.is_wip,
      is_deleted: item.is_deleted,
      is_disabled: item.is_disabled,
      is_clicked: item.is_clicked,
      is_selectable: item.is_selectable,
      is_selected: item.is_selected,
      is_hidden: item.is_hidden,
      is_match: item.is_match,
      is_open: item.is_open,
      parent: '',
      children: [],
      BATCH_BLK_SUFFIX: '',
      STEP: item.STEP.value,
      OPERATION: item.STEP.value + '. ' + item.OPERATION.value,
      OPERATION_BUSINESS: item.OPERATION_BUSINESS.value,
      RESOURCES: selectedScenario && (
        <ResourceCount
          is_open={
            selectedScenario.data.work_plans.byId[item.WORK_PLAN_ID.value]
              .is_open
          }
          item={item}
        />
      ),
      /*
       +
      JSON.stringify(resources.map((item) => item?.RESOURCE_NAME.value)) */ OPERATION_ID:
        '',
      MAX_QUANTITY_IN_BATCH: item.MAX_QUANTITY_IN_BATCH?.value ?? '',
      OPERATION_TYPE: item.OPERATION_TYPE.value,
      OPERATION_TYPE_ID: item.OPERATION_TYPE_ID.value,
      PROCESS: '',
      SITE: '',
      SITE_ABBREVIATION: '',
      SITE_ID: '',
      WORK_PLAN: '',
      WORK_PLAN_ID: '',
      OPERATION_INITIAL_START_TIME: '',
      OPERATION_INITIAL_DURATION: '',
      OPERATION_MIDDLE_START_TIME: '',
      OPERATION_MIDDLE_DURATION: '',
      OPERATION_END_START_TIME: '',
      OPERATION_END_DURATION: '',
      GSR: selectedScenario && (
        <ScrapReworkPercentage
          is_open={true}
          itemId={item.OPERATION.value}
          itemScrap={item.OPERATION_SCRAP.value}
          itemGood={item.OPERATION_GOOD.value}
          itemRework={item.OPERATION_REWORK.value}
        />
      ),
      SETUP_TIME: calculateOperationTime('SETUP_TIME', item),
      PROCESS_TIME: calculateOperationTime('PROCESS_TIME', item),
      PERSONNEL_TIME: calculateOperationTime('PERSONNEL_TIME', item),
      TS_FACTOR: item.TS_FACTOR.value || 1,
    };

    return newItem;
  };

  /**
   * Generates the tree to render in the table
   * SITE > PROCESS TYPE > WORKPLAN > OPERATION
   * Returns an array of product structure objects
   *
   * @param {WipTreeElement<SiteTree>} sitesObject - The sites tree object
   * @param {WipTreeElement<ProcessTypesTree>} processTypesObject - The process types tree object
   * @param {WipTreeElement<WorkPlanTree>} workPlansObject - The work plans tree object
   */
  const generateObjectWithChildren = (
    sitesObject: WipTreeElement<SiteTree>,
    processTypesObject: WipTreeElement<ProcessTypesTree>,
    workPlansObject: WipTreeElement<WorkPlanTree>,
    originalObject: WipTreeElement<OperationTree>,
  ): TProductStructure[] => {
    const objectWithChildren: any[] = [];
    const itemMap: { [itemId: string]: TProductStructure } = {};

    sitesObject.allIds.forEach((itemId: string) => {
      const item = sitesObject.byId[itemId];

      const newItem: TProductStructure = {
        id: item.SITE_ID.originalValue,
        area: 'sites',
        isExpanded: item.is_open,
        isDisabled: item.is_disabled,
        data: {
          key: item.SITE_ID.originalValue,
          title: item.SITE.value,
          is_wip: item.is_wip,
          is_deleted: item.is_deleted,
          is_disabled: item.is_disabled,
          is_clicked: item.is_clicked,
          is_selectable: item.is_selectable,
          is_selected: item.is_selected,
          is_hidden: item.is_hidden,
          is_match: item.is_match,
          is_open: item.is_open,
          parent: '',
          children: [],
          BATCH_BLK_SUFFIX: '',
          MAX_QUANTITY_IN_BATCH: '',
          OPERATION: item.SITE.value,
          OPERATION_BUSINESS: '',
          RESOURCES: '',
          OPERATION_ID: '',
          OPERATION_TYPE: '',
          OPERATION_TYPE_ID: '',
          PROCESS: '',
          SITE: '',
          SITE_ABBREVIATION: '',
          SITE_ID: '',
          STEP: '',
          WORK_PLAN: '',
          WORK_PLAN_ID: '',
          OPERATION_INITIAL_START_TIME: '',
          OPERATION_INITIAL_DURATION: '',
          OPERATION_MIDDLE_START_TIME: '',
          OPERATION_MIDDLE_DURATION: '',
          OPERATION_END_START_TIME: '',
          OPERATION_END_DURATION: '',
          GSR: '',
          SETUP_TIME: '',
          PROCESS_TIME: '',
          PERSONNEL_TIME: '',
          TS_FACTOR: '',
        },
        children: [],
      };

      objectWithChildren.push(newItem);
      itemMap[itemId] = newItem;
    });

    processTypesObject.allIds.forEach((itemId: string) => {
      const item = processTypesObject.byId[itemId];
      const siteId = item.SITE_ID.value;

      if (!sitesObject.byId[siteId]) return;

      const newItem: TProductStructure = {
        id: item.PROCESS_TYPE_ID.originalValue,
        area: 'process_types',
        isExpanded: item.is_open,
        isDisabled: item.is_disabled,
        data: {
          key: item.PROCESS_TYPE_ID.originalValue,
          title: item.PROCESS.value,
          is_wip: item.is_wip,
          is_deleted: item.is_deleted,
          is_disabled: item.is_disabled,
          is_clicked: item.is_clicked,
          is_selectable: item.is_selectable,
          is_selected: item.is_selected,
          is_hidden: item.is_hidden,
          is_match: item.is_match,
          is_open: item.is_open,
          parent: '',
          children: [],
          BATCH_BLK_SUFFIX: '',
          MAX_QUANTITY_IN_BATCH: '',
          OPERATION: item.PROCESS.value,
          OPERATION_BUSINESS: '',
          RESOURCES: '',
          OPERATION_TYPE_ID: '',
          OPERATION_ID: '',
          OPERATION_TYPE: '',
          PROCESS: '',
          SITE: '',
          SITE_ABBREVIATION: '',
          SITE_ID: '',
          STEP: '',
          WORK_PLAN: '',
          WORK_PLAN_ID: '',
          OPERATION_INITIAL_START_TIME: '',
          OPERATION_INITIAL_DURATION: '',
          OPERATION_MIDDLE_START_TIME: '',
          OPERATION_MIDDLE_DURATION: '',
          OPERATION_END_START_TIME: '',
          OPERATION_END_DURATION: '',
          GSR: '',
          SETUP_TIME: '',
          PROCESS_TIME: '',
          PERSONNEL_TIME: '',
          TS_FACTOR: '',
        },
        children: [],
      };

      objectWithChildren
        .find((obj) => obj.id === siteId)
        .children.push(newItem);
      itemMap[itemId] = newItem;
    });

    originalObject.allIds.forEach((itemId: string) => {
      const item = originalObject.byId[itemId];

      const siteId = item.SITE_ID.originalValue;
      if (!sitesObject.byId[siteId]) return;

      const processTypeId = item.PROCESS_TYPE_ID.value;
      const wp = workPlansObject.byId[item.WORK_PLAN_ID.value];
      const newItem: TProductStructure = {
        id: item.WORK_PLAN_ID.value,
        area: 'work_plans',
        isExpanded: wp.is_open,
        isDisabled: wp.is_disabled,
        data: {
          key: item.WORK_PLAN_ID.value,
          title: item.WORK_PLAN_ID.value,
          is_wip: wp.is_wip,
          is_deleted: wp.is_deleted,
          is_disabled: wp.is_disabled,
          is_clicked: wp.is_clicked,
          is_selectable: wp.is_selectable,
          is_selected: wp.is_selected,
          is_hidden: wp.is_hidden,
          is_match: wp.is_match,
          is_open: wp.is_open,
          parent: '',
          children: [],
          BATCH_BLK_SUFFIX: '',
          MAX_QUANTITY_IN_BATCH: '',
          OPERATION: wp.WORK_PLAN.value,
          OPERATION_BUSINESS: '',
          RESOURCES: '',
          OPERATION_ID: '',
          OPERATION_TYPE_ID: '',
          OPERATION_TYPE: '',
          PROCESS: '',
          SITE: '',
          SITE_ABBREVIATION: '',
          SITE_ID: '',
          STEP: item.STEP.value,
          WORK_PLAN: '',
          WORK_PLAN_ID: '',
          OPERATION_INITIAL_START_TIME: '',
          OPERATION_INITIAL_DURATION: '',
          OPERATION_MIDDLE_START_TIME: '',
          OPERATION_MIDDLE_DURATION: '',
          OPERATION_END_START_TIME: '',
          OPERATION_END_DURATION: '',
          GSR: '',
          SETUP_TIME: '',
          PROCESS_TIME: '',
          PERSONNEL_TIME: '',
          TS_FACTOR: '',
        },
        children: [],
      };

      if (!itemMap[processTypeId]) itemMap[processTypeId] = newItem;
      else if (
        !itemMap[processTypeId].children.some(
          (child) => child.id === newItem.id,
        )
      ) {
        itemMap[processTypeId].children.push(newItem);
        itemMap[item.WORK_PLAN_ID.value] = newItem;
      }
    });

    originalObject.allIds.forEach((itemId: string) => {
      const item = originalObject.byId[itemId];
      const workPlanId = item.WORK_PLAN_ID.value;

      const siteId = item.SITE_ID.originalValue;
      if (!sitesObject.byId[siteId]) return;

      const newItem: TProductStructure = {
        id: item.OPERATION_ID.value,
        area: 'operations',
        isExpanded: item.is_open,
        isDisabled: item.is_disabled,
        data: TransformProduct(item),
        children: [],
      };

      if (!newItem.data.is_deleted) {
        itemMap[workPlanId].children.push(newItem);

        itemMap[workPlanId].children.sort((a, b) => {
          const stepA = (a.data.STEP as number) || 0; // Use 0 as a default value if STEP.value is null or undefined
          const stepB = (b.data.STEP as number) || 0; // Use 0 as a default value if STEP.value is null or undefined

          return stepA - stepB;
        });

        itemMap[itemId] = newItem;
      }
    });

    return objectWithChildren;
  };

  if (!selectedScenario) return <>No Scenario</>;

  const objectWithChildren = generateObjectWithChildren(
    selectedScenario.data.sites,
    selectedScenario.data.process_types,
    selectedScenario.data.work_plans,
    selectedScenario.data.operations,
  );

  const TableHeaders: TableHeaderType[] = [
    {
      title: 'Operation',
      align: 'left',
      columnKey: 'OPERATION',
      columnWidth: '400px',
    },
    {
      title: 'Max Quantity in Batch',
      align: 'left',
      columnKey: 'MAX_QUANTITY_IN_BATCH',
    },
    {
      title: 'Operation ID',
      align: 'left',
      columnKey: 'OPERATION_BUSINESS',
    },
    {
      title: 'Resources',
      align: 'center',
      columnKey: 'RESOURCES',
    },
    {
      title: 'Setup Time',
      align: 'center',
      columnKey: 'SETUP_TIME',
    },
    {
      title: 'Process Time',
      align: 'center',
      columnKey: 'PROCESS_TIME',
    },
    {
      title: 'Personnel Time',
      align: 'center',
      columnKey: 'PERSONNEL_TIME',
    },
    {
      title: 'Operation Type',
      align: 'center',
      columnKey: 'OPERATION_TYPE',
    },
    {
      title: 'Scrap Rework',
      align: 'center',
      columnKey: 'GSR',
    },
    {
      title: 'Ts Factor',
      align: 'center',
      columnKey: 'TS_FACTOR',
    },
  ];

  const handleDoubleClick = (value: TableRowData) => {
    const area = value.area as TSimulationMenuOptions;
    if (area !== 'operations') return;

    setSimulationParams((prevState) => ({
      ...prevState,
      drawerOpen: true,
      formItem: value.id,
      formArea: area,
    }));
  };

  const handleClick = (value: TableRowData) => {
    if (!value.children) return;
    if (value.children.length <= 0) return;

    dispatch(
      changeExpanded({
        id: value.id,
        isExpanded: value.isExpanded ?? false,
        area: value.area as keyof WipTree,
      }),
    );
  };

  const handleMenuRight = (
    value: TableRowData,
    event?: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    const area = value.area as keyof WipTree;

    if (area !== 'operations' && area !== 'work_plans') return;

    event &&
      setAnchorEl({
        left: event.clientX,
        top: event.clientY,
      });

    setSelectedRow(value);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: 10,
          marginBottom: 10,
        }}
      >
        <MultiEditModal area="operations" keyProp="OPERATION_ID" name="OPERATION" />
      </div>
      <div style={{ marginBottom: 20 }}>
        <Input
          id="Filter"
          onChange={handleInputChangeDebounced}
          placeholder="Filter"
        />
      </div>

      <ContextMenu
        selectedRow={selectedRow}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        showRemove={selectedRow?.area !== 'work_plans'}
      />
      <Table
        headers={TableHeaders}
        width={'100%'}
        isSelectable={false}
        hasFilter={true}
        filterQuery={filterQuery}
        rows={objectWithChildren}
        typographyTag="textsmall"
        pointerCursor={true}
        onRowDoubleClick={(value) => handleDoubleClick(value)}
        onRowClick={(value) => handleClick(value)}
        onRowRightClick={(value, event) => handleMenuRight(value, event)}
        stickyHeader={true}
        headerVerticalAlign="bottom"
      />
    </>
  );
};

export default Operations;
